import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { Close, DeviceHub, Lightbulb, LightbulbOutlined, Tune } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  useColorScheme,
  Button,
  Container,
  Switch,
  AppBar,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import { use, useCallback, useEffect, useState } from 'react';
import { ColorModeContext } from '@/contexts/globalStateProvider';
import useIndexedDB from '@/services/database/useIndexedDB';
import { queryClient } from '@/lib/queryClient';
import DeviceDiagnostics from '@/components/Organisms/DeviceDiagnostics/DeviceDiagnostics';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import { VersionDisplay } from '../../components/Atoms/VersionDisplay.component';
import useAgentMutation from '@/services/agent/agent.mutations';
import { useQuery } from '@tanstack/react-query';
import { createAgentFeaturesByIdQuery } from '@/services/agent/agent.options';
import { createUserQueryOptions } from '@/services/auth/auth.options';

export const ThemeConfigurator = React.memo(function ThemeConfigurator() {

  const theme = useTheme();
  const IndexedDBAdapter = useIndexedDB()
  const [open, setOpen] = useState(false);
  const { mode, setMode } = useColorScheme();
  const agent = useQuery(createUserQueryOptions({ ticketid: 0, customerid: 0, limittocustomer: false }))
  const agentFeatures = useQuery(createAgentFeaturesByIdQuery({ AgentId: agent.data?.ContextUser?.AgentId }))
  const { agentFeatureMutation } = useAgentMutation();

  const isMobile = useMediaQuery('(max-width: 600px)');

  const toggleDrawer = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const [showDeviceDiagnostics, setShowDeviceDiagnostics] = useState<boolean>(false)
  const [databases, setDatabases] = useState<IDBDatabaseInfo[]>([]);
  const [storageUsage, setStorageUsage] = useState<number>(0);
  const [debuggerMode, setDebuggerMode] = useState<boolean>(localStorage.getItem('debug') === 'true');
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const getStorageUsage = useCallback(async () => {
    try {
      // Get all indexedDB databases
      const dbs = await window.indexedDB.databases();
      setDatabases(dbs);
      // console.log(dbs);
      let totalSize = 0;

      for (const db of dbs) {
        // Open each database
        const request = window.indexedDB.open(db.name || '');

        request.onsuccess = (event) => {
          const db = (event.target as IDBRequest<IDBDatabase>).result;
          if (!db) return;
          // Get all object stores
          const objectStoreNames = Array.from(db.objectStoreNames);

          objectStoreNames.forEach(storeName => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);

            // Get all records
            const getAllRequest = store.getAll();

            getAllRequest.onsuccess = () => {
              // Calculate size by converting to string and getting length
              const size = new TextEncoder().encode(
                JSON.stringify(getAllRequest.result)
              ).length;

              totalSize += size;
              setStorageUsage(totalSize);
              console.log(`Storage usage for ${db.name}: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
            };
          });
        };
      }
    } catch (error) {
      console.error('Error getting storage usage:', error);
      setLoading(false);
    }
  }, []);

  const DBReset = useCallback(async () => {
    try {
      // Get all databases
      console.log('resetting db');
      // Get all localStorage keys
      const keys = Object.keys(localStorage);

      // Remove all items except jwt, debug and public_key
      keys.forEach(key => {
        if (key !== 'jwt' && key !== 'debug' && key !== 'public_key') {
          localStorage.removeItem(key);
        }
      });
      IndexedDBAdapter.removeItem('dropSource');
      queryClient.clear();
      setDatabases([]);
      setStorageUsage(0);
    } catch (error) {
      console.error('Error resetting databases:', error);
      setDeleting(false);
    }
  }, [getStorageUsage, setStorageUsage, setDatabases]);

  useEffect(() => {
    getStorageUsage();
  }, [getStorageUsage]);

  const handleRefreshMetrics = useCallback(() => {
    setLoading(true);
    getStorageUsage();
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [getStorageUsage]);

  const handleDBReset = useCallback(() => {
    setDeleting(true);
    DBReset();
    setTimeout(() => {
      setDeleting(false);
    }, 1000);
  }, [DBReset]);

  return (
    <React.Fragment key={'right'}>
      <IconButton
        size={'small'}
        color={theme.palette.background.default}
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          bottom: 74,
          left: 8,
          zIndex: 100,
          backgroundColor: 'background.paper',
          border: `1px solid`,
        }}
      >
        <Tune fontSize={'large'} color={'inherit'} />
      </IconButton>
      <DrawerLayout
        anchor={'right'}
        open={open}
        onClose={toggleDrawer}
        hideBackdrop={false}
        transitionDuration={500}
        elevation={20}
        width={isMobile ? '100%' : '50%'}
        sx={{
          zIndex: 100,
        }}
        Header={
          <DrawerHeader
            close={toggleDrawer}
            title={'System Settings'}
          />
        }
      >
        <Container maxWidth='xl' sx={{
          overflowY: 'auto',
          mt: 5,
          display: 'flex',
          flexDirection: 'column',
          mb: 4,
        }}>

          <AppBar position="static" sx={{
            borderRadius: 2,
            mb: 4,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px',
            backgroundImage: 'none',
          }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant={'h6'} color={'text.primary'} textTransform={'uppercase'}>
                  Version
                </Typography>
                <VersionDisplay />
              </Box>
            </Toolbar>
          </AppBar>

          <AppBar position="static" sx={{
            borderRadius: 2,
            mb: 4,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px',
            backgroundImage: 'none',
          }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant={'h6'} color={'text.primary'} textTransform={'uppercase'}>
                  Environment
                </Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                  {/* {process.env.REACT_APP_SHOW_DEV_BANNER} */}
                  {JSON.stringify(process.env.NODE_ENV)}
                  {/* {JSON.stringify(import.meta)} */}
                  {JSON.stringify(import.meta.env.MODE)}
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>

          <AppBar position="static" sx={{
            borderRadius: 2,
            mb: 4,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px',
            backgroundImage: 'none',
          }}>
            <Toolbar sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
              <Typography variant={'h6'} color={'text.primary'} textTransform={'uppercase'} sx={{ mb: 2 }}>
                Theme
              </Typography>
              <FormControl fullWidth>
                <FormLabel id="demo-theme-toggle">Select Theme Mode</FormLabel>
                <RadioGroup
                  aria-labelledby="demo-theme-toggle"
                  name="theme-toggle"
                  row
                  value={mode}
                  onChange={(event) => {
                    setMode(event.target.value as 'system' | 'light' | 'dark')
                  }}
                  sx={{ justifyContent: 'space-around', width: '100%', mt: 1 }}
                >
                  <FormControlLabel value="system" sx={{ color: 'text.primary' }} control={<Radio />} label="System" />
                  <FormControlLabel value="light" sx={{ color: 'text.primary' }} control={<Radio />} label="Light" />
                  <FormControlLabel value="dark" sx={{ color: 'text.primary' }} control={<Radio />} label="Dark" />
                </RadioGroup>
              </FormControl>
            </Toolbar>
          </AppBar>

          <AppBar position="static" sx={{
            borderRadius: 2,
            mb: 4,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px',
            backgroundImage: 'none',
          }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant={'h6'} color={'text.primary'} textTransform={'uppercase'}>
                  Storage Usage
                </Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                  Total Storage Usage: {(storageUsage / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Button loading={loading} size='small' variant={'contained'} onClick={handleRefreshMetrics} sx={{ mr: 2 }}>Refresh Metrics</Button>
              <Button loading={deleting} size='small' variant={'contained'} color={'error'} onClick={handleDBReset} >Reset Database</Button>
            </Toolbar>
          </AppBar>

          <AppBar position="static" sx={{
            borderRadius: 2,
            mb: 4,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px',
            backgroundImage: 'none',
          }}>
            <Toolbar sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant={'h6'} color={'text.primary'}>Device Diagnostics</Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 4 }}>
                <FormControl>
                  <FormLabel id="device-diagnostics-toggle">Show Device Diagnostics</FormLabel>
                  <Switch
                    checked={showDeviceDiagnostics}
                    onChange={(event) => {
                      setShowDeviceDiagnostics(event.target.checked)
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel id="debugger-mode-toggle">Debug Mode</FormLabel>
                  <Switch
                    checked={debuggerMode}
                    onChange={(event) => {
                      setDebuggerMode(event.target.checked);
                      if (event.target.checked) {
                        localStorage.setItem('debug', 'true');
                        console.debug('Debug mode enabled');
                      } else {
                        localStorage.removeItem('debug');
                        console.debug('Debug mode disabled');
                      }
                    }}
                  />
                </FormControl>
              </Box>
            </Toolbar>
          </AppBar>
          {showDeviceDiagnostics && <DeviceDiagnostics />}

        </Container>
      </DrawerLayout>
    </React.Fragment >
  );
});
